import {
  SphereGeometry,
  TubeGeometry,
  BufferGeometry,
  BufferAttribute,
  Mesh,
  Group,
  DoubleSide,
  Curve,
  Vector3,
  Color,
  MeshPhysicalMaterial,
  TextureLoader
} from 'three'
import { setOpacity } from '../utilities/utilities'
import EyeTexture from '../textures/krakenEye.jpg'

// Here’s a case where Prettier doesn’t quite do the best job so we take the responsibility of formatting
// this ourselves.
//
// prettier-ignore
const h0 = 2, h1 = 1.6, r1 = 0.7, h2 = 0.7, r2 = 0.85, r3 = 0.7, r4 = 0.75
const BASE_VERTICES = [
  [0, h0, 0], //very top
  [r1, h1, 0], //top right
  [Math.sqrt((r1 * r1) / 2), h1, Math.sqrt((r1 * r1) / 2)], //top front right
  [0, h1, r1 * 1.1], //top front
  [-Math.sqrt((r1 * r1) / 2), h1, Math.sqrt((r1 * r1) / 2)], //top front left
  [-r1, h1, 0], //top left
  [-Math.sqrt((r1 * r1) / 2), h1, -Math.sqrt((r1 * r1) / 2)], //top back left
  [0, h1, -r1], //top back
  [Math.sqrt((r1 * r1) / 2), h1, -Math.sqrt((r1 * r1) / 2)], //top back right
  [r2, h2, 0], //middle right
  [Math.sqrt((r2 * r2) / 2), h2, Math.sqrt((r2 * r2) / 2)], //middle front right
  [0, h2 * 0.9, r2 * 1.2], //middle front
  [-Math.sqrt((r2 * r2) / 2), h2, Math.sqrt((r2 * r2) / 2)], //middle front left
  [-r2, h2, 0], //middle left
  [-Math.sqrt((r2 * r2) / 2), h2, -Math.sqrt((r2 * r2) / 2)], //middle back left
  [0, h2, -r2], //middle back
  [Math.sqrt((r2 * r2) / 2), h2, -Math.sqrt((r2 * r2) / 2)], //middle back right
  [r3, 0, 0], //bottom right
  [Math.sqrt((r3 * r3) / 2), 0, Math.sqrt((r3 * r3) / 2)], //bottom front right
  [0, 0 * 0.9, r3 * 1.1], //bottom front
  [-Math.sqrt((r3 * r3) / 2), 0, Math.sqrt((r3 * r3) / 2)], //bottom front left
  [-r3, 0, 0], //bottom left
  [-Math.sqrt((r3 * r3) / 2), 0, -Math.sqrt((r3 * r3) / 2)], //bottom back left
  [0, 0, -r3], //bottom back
  [Math.sqrt((r3 * r3) / 2), 0, -Math.sqrt((r3 * r3) / 2)], //bottom back right
  [r4, -r4, -r3], //face right
  [Math.sqrt((r4 * r4) / 2), -r4 - Math.sqrt((r4 * r4) / 2), -r3], //face front right
  [0, -2 * r4, -r3], //face front
  [-Math.sqrt((r4 * r4) / 2), -r4 - Math.sqrt((r4 * r4) / 2), -r3], //face front left
  [-r4, -r4, -r3], //face left
  [-Math.sqrt((r4 * r4) / 2), -Math.sqrt((r4 * r4) / 2), -r3], //face back left
  [0, 0, -r3], //face back
  [Math.sqrt((r4 * r4) / 2), -Math.sqrt((r4 * r4) / 2), -r3], //face back right
  [0, -r4, -r3 - 0.2] //very bottom
]
// prettier-ignore

/**
 * Creates a custom geometry out of a raw listing of vertices. Working this out on graph paper can be
 * a good first step here! Alternatively, you can write some code to generate these vertices, if the
 * shape that you have in mind can be computed in some way.
 */
const createKrakenHead = (baseColor) => {
  const geometry = new BufferGeometry()

  const BASE_COLORS = [
    [ baseColor[0]+0.5, baseColor[1]+0.5, baseColor[2]+0.5 ],
    [ baseColor[0]+0.3, baseColor[1]+0.3, baseColor[2]+0.3 ],
    [ baseColor[0]+0.2, baseColor[1]+0.2, baseColor[2]+0.2 ],
  ]

  // We build each triangle as a separate face, copying a vertex if needed. Remember, counterclockwise
  // is the “front.”
  //
  // prettier-ignore
  const vertices = new Float32Array([
    //top quadrant
    ...BASE_VERTICES[0], ...BASE_VERTICES[1], ...BASE_VERTICES[8],
    ...BASE_VERTICES[0], ...BASE_VERTICES[2], ...BASE_VERTICES[1],
    ...BASE_VERTICES[0], ...BASE_VERTICES[3], ...BASE_VERTICES[2],
    ...BASE_VERTICES[0], ...BASE_VERTICES[4], ...BASE_VERTICES[3],
    ...BASE_VERTICES[0], ...BASE_VERTICES[5], ...BASE_VERTICES[4],
    ...BASE_VERTICES[0], ...BASE_VERTICES[6], ...BASE_VERTICES[5],
    ...BASE_VERTICES[0], ...BASE_VERTICES[7], ...BASE_VERTICES[6],
    ...BASE_VERTICES[0], ...BASE_VERTICES[8], ...BASE_VERTICES[7],
    //middle quadrant
    ...BASE_VERTICES[1], ...BASE_VERTICES[9], ...BASE_VERTICES[16],
    ...BASE_VERTICES[1], ...BASE_VERTICES[16], ...BASE_VERTICES[8],
    ...BASE_VERTICES[8], ...BASE_VERTICES[16], ...BASE_VERTICES[15],
    ...BASE_VERTICES[8], ...BASE_VERTICES[15], ...BASE_VERTICES[7],
    ...BASE_VERTICES[7], ...BASE_VERTICES[15], ...BASE_VERTICES[14],
    ...BASE_VERTICES[7], ...BASE_VERTICES[14], ...BASE_VERTICES[6],
    ...BASE_VERTICES[6], ...BASE_VERTICES[14], ...BASE_VERTICES[13],
    ...BASE_VERTICES[6], ...BASE_VERTICES[13], ...BASE_VERTICES[5],
    ...BASE_VERTICES[5], ...BASE_VERTICES[13], ...BASE_VERTICES[12],
    ...BASE_VERTICES[5], ...BASE_VERTICES[12], ...BASE_VERTICES[4],
    ...BASE_VERTICES[4], ...BASE_VERTICES[12], ...BASE_VERTICES[3],
    ...BASE_VERTICES[3], ...BASE_VERTICES[12], ...BASE_VERTICES[11],
    ...BASE_VERTICES[3], ...BASE_VERTICES[11], ...BASE_VERTICES[10],
    ...BASE_VERTICES[3], ...BASE_VERTICES[10], ...BASE_VERTICES[2],
    ...BASE_VERTICES[2], ...BASE_VERTICES[10], ...BASE_VERTICES[9],
    ...BASE_VERTICES[2], ...BASE_VERTICES[9], ...BASE_VERTICES[1],
    //bottom quadrant
    ...BASE_VERTICES[9], ...BASE_VERTICES[17], ...BASE_VERTICES[24],
    ...BASE_VERTICES[9], ...BASE_VERTICES[24], ...BASE_VERTICES[16],
    ...BASE_VERTICES[16], ...BASE_VERTICES[24], ...BASE_VERTICES[23],
    ...BASE_VERTICES[16], ...BASE_VERTICES[23], ...BASE_VERTICES[15],
    ...BASE_VERTICES[15], ...BASE_VERTICES[23], ...BASE_VERTICES[22],
    ...BASE_VERTICES[15], ...BASE_VERTICES[22], ...BASE_VERTICES[14],
    ...BASE_VERTICES[14], ...BASE_VERTICES[22], ...BASE_VERTICES[21],
    ...BASE_VERTICES[14], ...BASE_VERTICES[21], ...BASE_VERTICES[13],
    ...BASE_VERTICES[13], ...BASE_VERTICES[21], ...BASE_VERTICES[20],
    ...BASE_VERTICES[13], ...BASE_VERTICES[20], ...BASE_VERTICES[12],
    ...BASE_VERTICES[12], ...BASE_VERTICES[20], ...BASE_VERTICES[19],
    ...BASE_VERTICES[12], ...BASE_VERTICES[19], ...BASE_VERTICES[11],
    ...BASE_VERTICES[11], ...BASE_VERTICES[19], ...BASE_VERTICES[10],
    ...BASE_VERTICES[10], ...BASE_VERTICES[19], ...BASE_VERTICES[18],
    ...BASE_VERTICES[10], ...BASE_VERTICES[18], ...BASE_VERTICES[17],
    ...BASE_VERTICES[10], ...BASE_VERTICES[17], ...BASE_VERTICES[9],
    //face quadrant
    ...BASE_VERTICES[17], ...BASE_VERTICES[25], ...BASE_VERTICES[32],
    ...BASE_VERTICES[17], ...BASE_VERTICES[32], ...BASE_VERTICES[24],
    ...BASE_VERTICES[24], ...BASE_VERTICES[32], ...BASE_VERTICES[31],
    ...BASE_VERTICES[24], ...BASE_VERTICES[31], ...BASE_VERTICES[23],
    ...BASE_VERTICES[23], ...BASE_VERTICES[31], ...BASE_VERTICES[30],
    ...BASE_VERTICES[23], ...BASE_VERTICES[30], ...BASE_VERTICES[22],
    ...BASE_VERTICES[22], ...BASE_VERTICES[30], ...BASE_VERTICES[29],
    ...BASE_VERTICES[22], ...BASE_VERTICES[29], ...BASE_VERTICES[21],
    ...BASE_VERTICES[21], ...BASE_VERTICES[29], ...BASE_VERTICES[28],
    ...BASE_VERTICES[21], ...BASE_VERTICES[28], ...BASE_VERTICES[20],
    ...BASE_VERTICES[20], ...BASE_VERTICES[28], ...BASE_VERTICES[19],
    ...BASE_VERTICES[19], ...BASE_VERTICES[28], ...BASE_VERTICES[27],
    ...BASE_VERTICES[19], ...BASE_VERTICES[27], ...BASE_VERTICES[26],
    ...BASE_VERTICES[19], ...BASE_VERTICES[26], ...BASE_VERTICES[18],
    ...BASE_VERTICES[18], ...BASE_VERTICES[26], ...BASE_VERTICES[25],
    ...BASE_VERTICES[18], ...BASE_VERTICES[25], ...BASE_VERTICES[17],
    //bottom
    ...BASE_VERTICES[33], ...BASE_VERTICES[24], ...BASE_VERTICES[25],
    ...BASE_VERTICES[33], ...BASE_VERTICES[25], ...BASE_VERTICES[26],
    ...BASE_VERTICES[33], ...BASE_VERTICES[26], ...BASE_VERTICES[27],
    ...BASE_VERTICES[33], ...BASE_VERTICES[27], ...BASE_VERTICES[28],
    ...BASE_VERTICES[33], ...BASE_VERTICES[28], ...BASE_VERTICES[29],
    ...BASE_VERTICES[33], ...BASE_VERTICES[29], ...BASE_VERTICES[30],
    ...BASE_VERTICES[33], ...BASE_VERTICES[30], ...BASE_VERTICES[31],
    ...BASE_VERTICES[33], ...BASE_VERTICES[31], ...BASE_VERTICES[32],
  ])

  geometry.setAttribute('position', new BufferAttribute(vertices, 3))

  // We coordinate our colors to match the corresopnding vertex.
  //
  // prettier-ignore
  let tiles = []
  for (let i = 0; i < 64; i++) {
    tiles.push(...BASE_COLORS[0], ...BASE_COLORS[1], ...BASE_COLORS[2])
  }
  const colors = new Float32Array(tiles)
  geometry.setAttribute('color', new BufferAttribute(colors, 3))

  // With every face having its “own” vertex (since vertices are always copied), the computed normals correspond
  // exactly to the faces’ directions, leading to a faceted look.
  geometry.computeVertexNormals()
  return geometry
}

class tentacleCurve extends Curve {
  constructor(counter) {
    super()
    this.scale = 1
    this.counter = counter
  }

  getPoint(t, optionalTarget = new Vector3()) {
    const tx = t * 3 - 1.5
    const ty = (1 - t) * Math.sin(2 * Math.PI * t + this.counter)
    const tz = 0
    return optionalTarget.set(tx, ty, tz).multiplyScalar(this.scale)
  }
}

class Kraken {
  constructor(radius, position, stop) {
    const baseColor = [Math.random() * 0.5, Math.random() * 0.5, Math.random() * 0.5]

    let material = new MeshPhysicalMaterial({ vertexColors: true })
    material.side = DoubleSide

    const facetedGeometry = createKrakenHead(baseColor)
    const facetedMesh = new Mesh(facetedGeometry, material)
    facetedMesh.rotateX(-Math.PI)
    facetedMesh.rotateY(Math.PI / 2)

    //Credit to 3DHitman for the eye texture: https://www.deviantart.com/3dhitman/art/TRUE-BLUE-eye-texture-206860165
    let texture = new TextureLoader().load(EyeTexture)
    material = new MeshPhysicalMaterial({ map: texture })
    const leftEyeGeometry = new SphereGeometry(0.2)
    const leftEye = new Mesh(leftEyeGeometry, material)
    leftEye.position.z = 0.62
    leftEye.rotation.set(0.7, -1.5, 1)
    const rightEyeGeometry = new SphereGeometry(0.2)
    const rightEye = new Mesh(rightEyeGeometry, material)
    rightEye.position.z = -0.62
    rightEye.rotation.set(-0.7, 1.5, 1)

    const path = new tentacleCurve(0)
    material = new MeshPhysicalMaterial({
      color: new Color(baseColor[0] + 0.3, baseColor[1] + 0.3, baseColor[2] + 0.3)
    })
    material.side = DoubleSide
    const tentacleGeometry = new TubeGeometry(path, 11, 0.2, 3)

    const d = -2.185,
      h = 0.77,
      r = 0.5
    const tentacle1 = new Mesh(tentacleGeometry, material)
    tentacle1.position.set(d, h + r, 0)
    const tentacle2 = new Mesh(tentacleGeometry, material)
    tentacle2.position.set(d, h - r, 0)
    tentacle2.rotateX(Math.PI / 8)
    const tentacle3 = new Mesh(tentacleGeometry, material)
    tentacle3.position.set(d, h, r)
    tentacle3.rotateX((2 * Math.PI) / 8)
    const tentacle4 = new Mesh(tentacleGeometry, material)
    tentacle4.position.set(d, h, -r)
    tentacle4.rotateX((3 * Math.PI) / 8)
    const tentacle5 = new Mesh(tentacleGeometry, material)
    tentacle5.position.set(d, h + Math.sqrt((r * r) / 2), Math.sqrt((r * r) / 2))
    tentacle5.rotateX((4 * Math.PI) / 8)
    const tentacle6 = new Mesh(tentacleGeometry, material)
    tentacle6.position.set(d, h - Math.sqrt((r * r) / 2), Math.sqrt((r * r) / 2))
    tentacle6.rotateX((5 * Math.PI) / 8)
    const tentacle7 = new Mesh(tentacleGeometry, material)
    tentacle7.position.set(d, h + Math.sqrt((r * r) / 2), -Math.sqrt((r * r) / 2))
    tentacle7.rotateX((6 * Math.PI) / 8)
    const tentacle8 = new Mesh(tentacleGeometry, material)
    tentacle8.position.set(d, h - Math.sqrt((r * r) / 2), -Math.sqrt((r * r) / 2))
    tentacle8.rotateX((7 * Math.PI) / 8)

    this.tentacles = new Group()
    this.tentacles.add(tentacle1, tentacle2, tentacle3, tentacle4, tentacle5, tentacle6, tentacle7, tentacle8)
    this.group = new Group()
    this.group.add(facetedMesh, leftEye, rightEye, this.tentacles)

    this.group.position.set(position.x, position.y, position.z)
    this.radius = radius
    this.group.scale.set(radius, radius, radius)
    this.velocity = { x: -0.3, y: 0 }
    this.maxVelocity = { y: 0.1 }
    this.stop = stop
    this.health = 100
    this.cooldown = { hit: 0 }
    this.type = 'kraken'
  }

  move(position) {
    if (this.group.position.y < position.y) {
      this.velocity.y = Math.min(this.velocity.y + 0.0015, this.maxVelocity.y)
    } else if (this.group.position.y > position.y) {
      this.velocity.y = Math.max(this.velocity.y - 0.0015, -this.maxVelocity.y)
    }
    this.group.position.x += this.velocity.x
    this.group.position.y += this.velocity.y
    this.group.rotation.x = -this.velocity.y * 7 + Math.PI / 2
  }

  animate(counter) {
    let i = 0
    for (const tentacle of this.tentacles.children) {
      const path = new tentacleCurve(counter / 15 + i)
      const tentacleGeometry = new TubeGeometry(path, 11, 0.2, 3)
      tentacle.geometry = tentacleGeometry
      i++
    }
  }

  updateCooldowns() {
    if (this.cooldown.hit > 0) {
      this.cooldown.hit--
      if (this.cooldown.hit === 0) {
        setOpacity(this, 1)
      }
    }
  }
}

export default Kraken
